import React, { useCallback, useMemo, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/store";
import { ADMIN_LANG } from "../../../settings";
import AdminLoader from "../AdminLoader";
import { closeModal, importUsersExcel } from "../../../app/slices/modals/userImportModal";
import { DataParams } from "../../../types/admin";

const UserImportModal = () => {
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const { isOpen, loading, error, result } = useAppSelector((state) => state.userImportModal);
    const [file, setFile] = useState<File | null>(null);

    const params: DataParams = useMemo(() => ({
        page: searchParams.get("page") || undefined,
        sq: searchParams.get("sq") || undefined,
        sort_direction: searchParams.get("sort_direction") || undefined,
        sort_value: searchParams.get("sort_value") || undefined,
        team: searchParams.get("team") || undefined,
        company: searchParams.get("company") || undefined,
        is_active: searchParams.get("is_active") || undefined,
        is_staff: searchParams.get("is_staff") || undefined,
        is_superuser: searchParams.get("is_superuser") || undefined
    }), [searchParams]);

    const handleClose = useCallback(() => {
        setFile(null);
        dispatch(closeModal());
    }, [dispatch]);

    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!file) {
            return;
        }
        const formData = new FormData();
        formData.append("file", file);
        dispatch(importUsersExcel({ formData, params }));
    }, [dispatch, file, params]);

    const hasIssues = (result?.errors?.length || 0) > 0 || (result?.warnings?.length || 0) > 0;

    return (
        <Modal size="lg" show={isOpen} onHide={handleClose} className="admin-modal">
            {loading && <AdminLoader />}
            <Modal.Header closeButton>
                <Modal.Title>{ADMIN_LANG.import_users_excel}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <Alert variant="danger" dismissible>
                        <div dangerouslySetInnerHTML={{ __html: error }} />
                    </Alert>
                )}
                {result && (
                    <Alert variant={hasIssues ? "warning" : "success"}>
                        <div>
                            {ADMIN_LANG.import_summary}: {result.created} {ADMIN_LANG.created}, {result.updated} {ADMIN_LANG.updated}, {result.skipped} {ADMIN_LANG.skipped}
                        </div>
                        {result.errors && result.errors.length > 0 && (
                            <div className="mt-2">
                                <strong>{ADMIN_LANG.import_errors}</strong>
                                <ul className="mb-0">
                                    {result.errors.map((issue, index) => (
                                        <li key={`error-${index}`}>
                                            {ADMIN_LANG.row} {issue.row}: {issue.messages.join(" ")}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {result.warnings && result.warnings.length > 0 && (
                            <div className="mt-2">
                                <strong>{ADMIN_LANG.import_warnings}</strong>
                                <ul className="mb-0">
                                    {result.warnings.map((issue, index) => (
                                        <li key={`warning-${index}`}>
                                            {ADMIN_LANG.row} {issue.row}: {issue.messages.join(" ")}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </Alert>
                )}
                <Form id="user-import-form" onSubmit={handleSubmit}>
                    <Form.Group controlId="user-import-file">
                        <Form.Label>{ADMIN_LANG.import_users_excel_help}</Form.Label>
                        <Form.Control
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                if (event.target.files && event.target.files.length > 0) {
                                    setFile(event.target.files[0]);
                                } else {
                                    setFile(null);
                                }
                            }}
                            required
                        />
                        <Form.Text className="help-text">{ADMIN_LANG.import_users_excel_columns}</Form.Text>
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-primary" onClick={handleClose}>
                    {ADMIN_LANG.close}
                </Button>
                <Button variant="primary" type="submit" form="user-import-form" disabled={!file || loading}>
                    {ADMIN_LANG.import_users_excel}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default UserImportModal;
